// Import the helper function
import { executeQuery } from '../../utilities/helper.js';

/**
 * MR Components - Custom elements for theme functionality
 */

/**
 * mr-modal - A reusable modal/popup component
 * Based on LoadQuickview patterns but without PhotoSwipe dependency
 *
 * Usage:
 *   <mr-modal id="my-modal">
 *     <div class="popup-quick-view__inner" data-quick-view-inner>
 *       <!-- Modal content -->
 *       <button data-popup-close>Close</button>
 *     </div>
 *   </mr-modal>
 *
 * Methods:
 *   - show(): Show the modal
 *   - hide(): Hide the modal
 */
class MRModal extends HTMLElement {
  constructor() {
    super();

    // Initialize properties
    this.popupInner = null;
    this.closeButtons = [];
    this.overlay = null;
    this.a11y = window.theme?.a11y;
    this.initialized = false;

    // Bind methods to preserve 'this' context
    this.closePopupEvent = (event) => this.closePopup(event);
    this.closeOnAnimationEndEvent = (event) => this.closeOnAnimationEnd(event);
    this.closeOnEscapeEvent = (event) => this.closeOnEscape(event);
    this.handleOverlayClick = (event) => this.handleOverlayClick(event);
  }

  connectedCallback() {
    // Prevent multiple initializations
    if (this.initialized) return;

    // Set initial hidden state (styling is in SCSS)
    this.classList.add('mr-modal--hidden');

    // Find inner element
    this.popupInner = this.querySelector('[data-quick-view-inner]');
    if (!this.popupInner) {
      console.warn('mr-modal: [data-quick-view-inner] not found');
      return;
    }

    // Find close buttons
    this.closeButtons = Array.from(
      this.popupInner.querySelectorAll('[data-popup-close]')
    );

    // Create overlay
    this.overlay = this.createOverlay();

    // Initialize event listeners
    this.init();

    this.initialized = true;
  }

  disconnectedCallback() {
    // Cleanup when element is removed from DOM
    this.destroy();
  }

  createOverlay() {
    // Check if overlay already exists (for multiple modals)
    const modalId = this.id || 'mr-modal';
    const overlayId = `mr-modal-overlay-${modalId}`;
    let overlay = document.getElementById(overlayId);

    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = overlayId;
      overlay.className = 'mr-modal-overlay mr-modal-overlay--hidden';
      overlay.setAttribute('data-modal-id', modalId);
      document.body.appendChild(overlay);
    }

    return overlay;
  }

  init() {
    // Set up close button handlers (similar to LoadQuickview.initEventListeners)
    this.closeButtons.forEach((closeBtn) => {
      closeBtn.addEventListener('keyup', (event) => {
        if (
          event.code === window.theme?.keyboardKeys?.ENTER ||
          event.code === window.theme?.keyboardKeys?.NUMPADENTER ||
          event.code === window.theme?.keyboardKeys?.SPACE
        ) {
          this.closePopup(event);
        }
      });
      closeBtn.addEventListener('click', this.closePopupEvent);
    });

    // Set up overlay click handler (closes when clicking outside)
    this.overlay.addEventListener('click', this.handleOverlayClick);

    // Set up escape key handler (similar to LoadQuickview.closeOnEscape)
    document.addEventListener('keyup', this.closeOnEscapeEvent);

    // Set up animation end handler (similar to LoadQuickview.closeOnAnimationEnd)
    this.addEventListener('animationend', this.closeOnAnimationEndEvent);
  }

  /**
   * Show the modal
   * Public method that can be called programmatically
   */
  show() {
    if (!this.initialized) {
      console.warn('mr-modal: Element not initialized');
      return;
    }

    // Show modal (styling is in SCSS)
    this.classList.remove('mr-modal--hidden');
    this.classList.add('mr-modal--visible');
    this.overlay.classList.remove('mr-modal-overlay--hidden');

    // Lock scroll (similar to LoadQuickview scroll locking)
    document.dispatchEvent(
      new CustomEvent('theme:scroll:lock', {
        bubbles: true,
        detail: this.popupInner,
      })
    );

    // Trap focus for accessibility (similar to LoadQuickview.init)
    if (this.a11y?.trapFocus) {
      this.a11y.trapFocus({ container: this.popupInner });
    }

    // Add body class (similar to LoadQuickview adding js-quick-view-visible)
    // Use a consistent class name based on the modal ID
    const modalId = this.id || 'mr-modal';
    const bodyClass =
      modalId === 'fitting-room-popup'
        ? 'js-fitting-room-visible'
        : `js-${modalId}-visible`;
    document.body.classList.add(bodyClass);

    // Dispatch popup open event (similar to LoadQuickview.init)
    document.dispatchEvent(
      new CustomEvent('theme:popup:open', { bubbles: true })
    );

    // Animate in (similar to LoadQuickview.animateInQuickview)
    this.animateIn();
  }

  /**
   * Hide the modal
   * Public method that can be called programmatically
   */
  hide() {
    this.closePopup();
  }

  animateIn() {
    this.classList.add('popup-quick-view--animate-in', 'mr-modal--animate-in');

    // Handle animation end (similar to LoadQuickview.handleAnimatedState)
    const handleAnimationEnd = (event) => {
      if (
        event.animationName === 'quickViewAnimateInUp' ||
        event.animationName === 'quickViewAnimateInLeft' ||
        event.animationName === 'mrModalFadeIn'
      ) {
        this.classList.add('popup-quick-view--animated', 'mr-modal--animated');
        this.classList.remove(
          'popup-quick-view--animate-in',
          'mr-modal--animate-in'
        );
        this.removeEventListener('animationend', handleAnimationEnd);
      }
    };

    this.addEventListener('animationend', handleAnimationEnd, { once: true });
  }

  closePopup(event) {
    event?.preventDefault();

    // Add animate-out class (similar to LoadQuickview.closePopup)
    this.classList.add(
      'popup-quick-view--animate-out',
      'mr-modal--animate-out'
    );
    this.classList.remove('popup-quick-view--animated', 'mr-modal--animated');
  }

  closeOnAnimationEnd(event) {
    // Handle close animation (similar to LoadQuickview.closeOnAnimationEnd)
    if (
      event.animationName === 'quickViewAnimateOutRight' ||
      event.animationName === 'quickViewAnimateOutDown' ||
      event.animationName === 'mrModalFadeOut'
    ) {
      // Hide modal (styling is in SCSS)
      this.classList.add('mr-modal--hidden');
      this.classList.remove('mr-modal--visible');
      this.overlay.classList.add('mr-modal-overlay--hidden');
      this.classList.remove(
        'popup-quick-view--animate-out',
        'mr-modal--animate-out'
      );

      // Remove body class
      const modalId = this.id || 'mr-modal';
      const bodyClass =
        modalId === 'fitting-room-popup'
          ? 'js-fitting-room-visible'
          : `js-${modalId}-visible`;
      document.body.classList.remove(bodyClass);

      // Unlock scroll (similar to LoadPhotoswipe.onClose)
      document.dispatchEvent(
        new CustomEvent('theme:scroll:unlock', { bubbles: true })
      );

      // Remove focus trap
      if (this.a11y?.removeTrapFocus) {
        this.a11y.removeTrapFocus();
      }

      // Dispatch close event
      const closeEvent = new CustomEvent('theme:mr-modal:closed', {
        bubbles: true,
        detail: { modal: this },
      });
      document.dispatchEvent(closeEvent);
    }
  }

  closeOnEscape(event) {
    // Similar to LoadQuickview.closeOnEscape
    const modalId = this.id || 'mr-modal';
    const bodyClass =
      modalId === 'fitting-room-popup'
        ? 'js-fitting-room-visible'
        : `js-${modalId}-visible`;
    const isModalVisible = document.body.classList.contains(bodyClass);
    const isNotificationVisible = document.body.classList.contains(
      'notification-popup-visible'
    );

    if (
      event.code === window.theme?.keyboardKeys?.ESCAPE &&
      isModalVisible &&
      !isNotificationVisible
    ) {
      this.closePopup(event);
    }
  }

  handleOverlayClick(event) {
    if (event.target === this.overlay) {
      this.closePopup(event);
    }
  }

  destroy() {
    // Cleanup (similar to LoadQuickview destroy pattern)
    document.removeEventListener('keyup', this.closeOnEscapeEvent);
    this.removeEventListener('animationend', this.closeOnAnimationEndEvent);

    this.closeButtons.forEach((closeBtn) => {
      closeBtn.removeEventListener('click', this.closePopupEvent);
    });

    if (this.overlay) {
      this.overlay.removeEventListener('click', this.handleOverlayClick);
    }

    // Remove body class
    const modalId = this.id || 'mr-modal';
    const bodyClass =
      modalId === 'fitting-room-popup'
        ? 'js-fitting-room-visible'
        : `js-${modalId}-visible`;
    document.body.classList.remove(bodyClass);

    // Remove focus trap
    if (this.a11y?.removeTrapFocus) {
      this.a11y.removeTrapFocus();
    }

    this.initialized = false;
  }
}

// Register the custom element
if (!customElements.get('mr-modal')) {
  customElements.define('mr-modal', MRModal);
}

export default MRModal;

/**
 * @typedef {Object} SwatchData
 * @property {string} productId - The product ID (required). Used for label 'for' attribute, data-swatch, and data-tooltip.
 * @property {string} [id] - Alternative to productId.
 * @property {string} [inputId] - Custom input ID. If not provided, will be auto-generated.
 *
 * @property {string} [url] - Product URL for the link href and data-product-url attribute.
 * @property {string} [productUrl] - Alternative to url for product URL.
 *
 * @property {string|Object} [color] - Color object or string. If object, should have color_name property.
 * @property {string} [color.color_name] - Color name from color object.
 * @property {string} [colorName] - Direct color name (alternative to color.color_name).
 * @property {string} [colorValue] - Color value text (alternative to colorName).
 * @property {string} [value] - Option value. Used for input value and as fallback for color name.
 * @property {string} [optionValue] - Alternative to value for option value.
 *
 *
 * @property {boolean} [isActive] - Whether this swatch is currently selected/active.
 * @property {boolean} [selected] - Alternative to isActive for selected state.
 *
 * @property {string} [backgroundImage] - Background image URL for swatch (takes priority over backgroundColor).
 * @property {string} [swatchImage] - Alternative to backgroundImage.
 * @property {string} [colour_swatch_image] - Alternative to backgroundImage (matches Liquid metafield name).
 *
 * @property {string} [backgroundColor] - Background color for swatch (used if no image provided).
 * @property {string} [swatchColor] - Alternative to backgroundColor.
 * @property {string} [colorHex] - Alternative to backgroundColor (hex color code).
 * @property {string} [colour_swatch_hex] - Alternative to backgroundColor (matches Liquid metafield name).
 */

/**
 * mr-color-swatches - A custom element for rendering color swatches dynamically
 *
 * Usage:
 *   <mr-color-swatches data-swatch-style="circle"></mr-color-swatches>
 *
 * The component will:
 * 1. Automatically detect the current product from the page context
 * 2. Call an API to fetch color swatch data
 * 3. Render color swatches dynamically
 *
 * Expected API Response:
 * {
 *   "swatches": [
 *     {
 *       "productId": "123456",
 *       "url": "/products/product-handle",
 *       "colorName": "Red",
 *       "backgroundColor": "#ff0000",
 *       "value": "Red"
 *     }
 *   ]
 * }
 */

/**
 * Fetches product detail data from Fredhopper API
 * @param {string} productId - The product ID to fetch details for
 * @returns {Promise<Object>} The API response data
 */
async function fetchFhrProductDetail(productId) {
  if (!window.mrRecommendations) {
    window.mrRecommendations = {};
  }
  if (!window.mrRecommendations.productDetails) {
    window.mrRecommendations.productDetails = {};
  }

  const apiBaseUrl = process.env.API_BASE_URL;
  // 'fh_suppress=modifications:block' tells Fredhopper to ignore blocking result-modifications for this
  // query, so PDPs of out-of-stock colours are still returned and their sibling swatches render.
  // Must stay on the 'detail' (PDP) query only — on lister/search queries it would disable OOS blocking.
  const fhrProductDetailApiUrl = `${apiBaseUrl}/public/v1/fredhopper/query?fh_location=%2F%2Fcatalog01%2Fen_GB&fh_view=detail&fh_secondid=${productId}&fh_suppress=modifications%3Ablock`;

  const response = await fetch(fhrProductDetailApiUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`API request failed: ${response.statusText}`);
  }

  const data = await response.json();
  console.log('colour swatch data :', data);

  // Store data in global object by productId
  // This data will be used by the MR Fredhopper product recommendations app to fetch the recommendation data
  window.mrRecommendations.productDetails[productId] = data;

  return data;
}

/**
 * Decodes Shopify encodedVariantAvailability (e.g. "v1_0-7" or "v1_0:0-2,1:2,") into an array of option value index combinations.
 * No external library. Format: v1_ prefix, ":" separates options, "," separates segments, "-" is range, " " is gap.
 * @param {string} encoded - Raw string from Product.encodedVariantAvailability
 * @returns {number[][]} Array of combinations; each combination is [option0ValueIndex, option1ValueIndex, ...]
 * @example decodeEncodedVariantAvailability('v1_0-7') // [[0],[1],...,[7]]
 * @example decodeEncodedVariantAvailability('v1_0:0-2,1:2,') // [[0,0],[0,1],[0,2],[1,2]]
 */
function decodeEncodedVariantAvailability(encoded) {
  if (!encoded || typeof encoded !== 'string') return [];
  let s = encoded.startsWith('v1_') ? encoded.slice(3) : encoded;
  if (!s.length) return [];

  function expandRange(part) {
    const out = [];
    const tokens = part.split(/\s+/);
    for (const t of tokens) {
      const dash = t.indexOf('-');
      if (dash === -1) {
        const n = parseInt(t, 10);
        if (!Number.isNaN(n)) out.push(n);
      } else {
        const a = parseInt(t.slice(0, dash), 10);
        const b = parseInt(t.slice(dash + 1), 10);
        if (!Number.isNaN(a) && !Number.isNaN(b))
          for (let i = a; i <= b; i++) out.push(i);
      }
    }
    return out;
  }

  const combinations = [];
  const segments = s.split(',');
  let prefix = [];

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    if (seg === '') {
      if (combinations.length > 0)
        prefix = combinations[combinations.length - 1].slice(0, -1);
      continue;
    }
    const parts = seg.split(':');
    if (parts.length === 0) continue;
    const lastPart = parts[parts.length - 1];
    const rest = parts
      .slice(0, -1)
      .map((p) => parseInt(p, 10))
      .filter((n) => !Number.isNaN(n));
    const lastValues = expandRange(lastPart);
    for (const v of lastValues) {
      const combo =
        prefix.length > 0 && rest.length === 0
          ? [...prefix, v]
          : rest.length > 0
            ? [...rest, v]
            : [v];
      combinations.push(combo);
    }
    if (lastValues.length > 0)
      prefix = (
        rest.length > 0 ? [...rest, lastValues[0]] : [lastValues[0]]
      ).slice(0, -1);
  }

  return combinations;
}

/**
 * Fetches product data (images, handle, variants, encoded availability, options) from Shopify Storefront API
 * @param {Array<{product_id: string|number}>} swatches - Array of swatch objects from Fredhopper API swatch_info field
 * @returns {Promise<{productFrontImages: Object<string, string>, productHandles: Object<string, string>, productEncodedVariantAvailability: Object<string, string>, productDecodedVariantAvailability: Object<string, Record<string, Record<string, boolean>>>, productOptions: Object<string, Array<{name: string, values: string[]}>}>}
 * @example
 * const { productDecodedVariantAvailability } = await fetchProductStorefrontData(swatches);
 * // productDecodedVariantAvailability[productId] = { Size: { '8': true, '10': true, ... } }
 * // Fast check when option is selected: productDecodedVariantAvailability[productId]?.['Size']?.['10'] === true
 */
async function fetchProductStorefrontData(swatches) {
  if (!swatches || !Array.isArray(swatches) || swatches.length === 0) {
    console.warn('fetchProductStorefrontData: No swatches provided');
    return {};
  }

  // Build array of product IDs in GID format
  const productIds = swatches
    .map((swatch) => {
      const productId = swatch?.product_id;
      if (!productId) return null;

      // Convert to string and then to Shopify GID format if needed
      const productIdStr = String(productId);
      return productIdStr.startsWith('gid://')
        ? productIdStr
        : `gid://shopify/Product/${productIdStr}`;
    })
    .filter(Boolean);

  if (productIds.length === 0) {
    console.warn('fetchProductStorefrontData: No valid product IDs found');
    return {};
  }

  // Build GraphQL query using nodes query (cleaner than aliases)
  const graphQLQuery = `
    query GetProductData {
      nodes(ids: [${productIds.map((id) => `"${id}"`).join(', ')}]) {
        ... on Product {
          id
          handle
          images(first: 50) {
            edges {
              node {
                url(transform: { maxWidth: 100, preferredContentType: WEBP })
              }
            }
          }
          encodedVariantAvailability
          options(first: 50) {
            name
            optionValues {
              name
            }
          }
        }
      }
    }
  `;

  try {
    // Execute the GraphQL query using the helper function
    const response = await executeQuery(graphQLQuery);

    if (!response || !response.data || !response.data.nodes) {
      console.error(
        'fetchProductStorefrontData: Invalid API response',
        response
      );
      return {};
    }

    // Build the productFrontImages object: {[productId]: imageUrl}
    const productFrontImages = {};

    const productHandles = {};
    const productEncodedVariantAvailability = {};
    const productDecodedVariantAvailability = {};
    const productOptions = {};

    response.data.nodes.forEach((product) => {
      if (!product || !product.id || !product.images) return;
      const cleanProductId = product.id.replace('gid://shopify/Product/', '');

      // Find the first image that contains "Product_Front" in the URL (case-insensitive)
      const frontImage = product.images.edges.find((edge) => {
        const imageUrl = edge?.node?.url || '';
        return imageUrl.toLowerCase().includes('product_front');
      });

      if (frontImage) {
        // Store the URL using the clean productId (without gid:// prefix)
        // Append width param to avoid loading full-size images for small swatches
        const imgUrl = frontImage.node.url;
        productFrontImages[cleanProductId] =
          imgUrl + (imgUrl.includes('?') ? '&' : '?') + 'width=200';
      }

      productHandles[cleanProductId] = product.handle;

      // Encoded string: e.g. "v1_0-7" = option 0, value indices 0–7 available. Decoded below with decodeEncodedVariantAvailability (no extra library).
      if (product.encodedVariantAvailability != null) {
        productEncodedVariantAvailability[cleanProductId] =
          product.encodedVariantAvailability;
      }

      // Options in order; optionValues are { name } so we get values array. Indices match encodedVariantAvailability (e.g. Size: [8,10,...,22] → 0–7).
      if (product.options?.length) {
        productOptions[cleanProductId] = product.options.map((opt) => ({
          name: opt.name,
          values: (opt.optionValues || []).map((ov) => ov.name),
        }));
      }

      // Decoded: { [optionName]: { [value]: true } } for O(1) "is this option value available?" when user selects an option.
      if (
        product.encodedVariantAvailability != null &&
        productOptions[cleanProductId]
      ) {
        const indexCombos = decodeEncodedVariantAvailability(
          product.encodedVariantAvailability
        );
        const opts = productOptions[cleanProductId];
        const byOption = {};
        for (const combo of indexCombos) {
          combo.forEach((valueIndex, optionIndex) => {
            const option = opts[optionIndex];
            const value = option?.values?.[valueIndex] ?? '';
            if (!option?.name) return;
            if (!byOption[option.name])
              byOption[option.name] = Object.create(null);
            byOption[option.name][value] = true;
          });
        }
        productDecodedVariantAvailability[cleanProductId] = byOption;
      }
    });

    return {
      productFrontImages,
      productHandles,
      productEncodedVariantAvailability,
      productDecodedVariantAvailability,
      productOptions,
    };
  } catch (error) {
    console.error('fetchProductStorefrontData: Error fetching images', error);
    return {};
  }
}

/**
 * Returns the set of product IDs that have the given option value available for sale.
 * Use when an option is selected: disable the colour swatch for any product ID not in this set.
 * @param {string[]} productIds - Product IDs to check (e.g. current swatch/product set)
 * @param {string} optionName - Option name (e.g. 'Size')
 * @param {string} value - Option value (e.g. '10')
 * @param {Record<string, Record<string, Record<string, boolean>>>} productDecodedVariantAvailability - From fetchProductStorefrontData
 * @returns {Set<string>} Product IDs that have this option value in stock (use .has(id) to check; disable swatch when !set.has(productId))
 * @example
 * const productIdsWithSize10 = getProductIdsWithOptionAvailable(productIds, 'Size', '10', productDecodedVariantAvailability);
 * swatches.forEach(swatch => { swatch.disabled = !productIdsWithSize10.has(swatch.productId); });
 */
function getProductIdsWithOptionAvailable(
  productIds,
  optionName,
  value,
  productDecodedVariantAvailability
) {
  const out = new Set();
  if (!productIds?.length || !productDecodedVariantAvailability) return out;
  for (const id of productIds) {
    if (productDecodedVariantAvailability[id]?.[optionName]?.[value] === true)
      out.add(id);
  }
  return out;
}

/**
 * Extracts swatch data from Fredhopper API response
 * @param {Object} apiData - The API response data
 * @param {string} currentProductId - The current product ID to determine which swatch is active
 * @returns {Promise<{swatchesData: SwatchData[], productDecodedVariantAvailability: Object, productIds: string[]} | []>}
 * @example
 * // Example return format:
 * [
 *   {
 *     productId: '7754693378091',
 *     id: '7754693378091',
 *     url: '/products/7754693378091',
 *     productUrl: '/products/7754693378091',
 *     colorName: 'Wildberry',
 *     value: 'Wildberry',
 *     backgroundImage: '//cdn.shopify.com/.../image.jpg',
 *     backgroundColor: '#ff0000',
 *     isActive: true,
 *     selected: true,
 *   },
 *   {
 *     productId: '7757911556139',
 *     id: '7757911556139',
 *     url: '/products/7757911556139',
 *     productUrl: '/products/7757911556139',
 *     colorName: 'Wildberry',
 *     value: 'Wildberry',
 *     backgroundImage: '//cdn.shopify.com/.../image.jpg',
 *     backgroundColor: '',
 *     isActive: false,
 *     selected: false,
 *   }
 * ]
 */
async function extractSwatchData(apiData, currentProductId) {
  try {
    // Navigate to: universes.universe[1].items-section.items.item[0]
    const universe = apiData?.universes?.universe?.[1];
    if (!universe) {
      console.warn('mr-color-swatches: Universe not found in API response');
      return [];
    }

    const item = universe?.['items-section']?.items?.item?.[0];
    if (!item || !item.attribute) {
      console.warn(
        'mr-color-swatches: Item or attributes not found in API response'
      );
      return [];
    }

    // Find the attribute with name "swatch_info"
    const swatchInfoAttribute = item.attribute.find(
      (attr) => attr.name === 'swatch_info'
    );
    if (!swatchInfoAttribute || !swatchInfoAttribute.value?.[0]?.value) {
      console.warn(
        'mr-color-swatches: swatch_info attribute not found in API response'
      );
      return [];
    }

    // Parse the JSON string from value[0].value
    const swatchDataString = swatchInfoAttribute.value[0].value;
    const swatches = JSON.parse(swatchDataString);

    if (!Array.isArray(swatches)) {
      console.warn('mr-color-swatches: Parsed swatch data is not an array');
      return [];
    }

    // Old approach: also works if FH does not include the active swatch as the first item in the array.
    // Current issue is the swatch order does not match with what provided from FH.

    // FH API has updated 'swatch_info' to include 'active' colour swatch as the first item in the array.
    // Filter out duplicate colour swatches in case some 'swatch_info' attributes do not have this change yet.
    // const seenProductIds = new Set();
    // const dedupedSwatches = swatches.filter((item) => {
    //   if (seenProductIds.has(item.product_id)) return false;
    //   seenProductIds.add(item.product_id);
    //   return true;
    // });

    // Because FH returns an extra swatch (active swatch) as the first item, we can always remove the first item from the array.
    // The downside of this is FH must always include this extra swatch.
    // The benefit is making the logic much simpler while still keeping the correct swatch order from FH.
    const dedupedSwatches = swatches.slice(1);

    // WORKAROUND: Fetch PDP colour swatch thumbnail image (Product_Front product images) from Shopify Storefront API
    // TODO: When Fredhopper API includes Product_Front product image in 'swatch_info' field, this can be removed
    const { productFrontImages, productDecodedVariantAvailability } =
      await fetchProductStorefrontData(dedupedSwatches);

    const productIds = dedupedSwatches
      .map((s) => String(s.product_id || ''))
      .filter(Boolean);

    // Transform each swatch to SwatchData format
    const swatchesData = dedupedSwatches.map((swatch) => {
      const productId = String(swatch.product_id || '');
      const colorName = swatch.colour_name || '';
      const backgroundImage =
        productFrontImages[productId] || swatch.colour_image || '';
      const backgroundColor = swatch.colour_hex ? `#${swatch.colour_hex}` : '';
      const productHandle = swatch.handle || productId;

      // Construct product URL from product ID
      // Note: You may need to adjust this based on your URL structure
      const url = `/products/${productHandle}`;

      return {
        productId: productId,
        id: productId,
        url: url,
        productUrl: url,
        colorName: colorName,
        value: colorName,
        backgroundImage: backgroundImage,
        backgroundColor: backgroundColor,
        isActive: productId === String(currentProductId),
        selected: productId === String(currentProductId),
      };
    });

    return {
      swatchesData,
      productDecodedVariantAvailability:
        productDecodedVariantAvailability || {},
      productIds,
    };
  } catch (error) {
    console.error('mr-color-swatches: Error transforming API response', error);
    return {
      swatchesData: [],
      productDecodedVariantAvailability: {},
      productIds: [],
    };
  }
}

class MRColorSwatches extends HTMLElement {
  constructor() {
    super();

    // Initialize properties
    this.swatchesData = null;
    this.initialized = false;
  }

  connectedCallback() {
    // Prevent multiple initializations
    if (this.initialized) return;

    // Initialize
    this.init();

    this.initialized = true;
  }

  disconnectedCallback() {
    // Cleanup when element is removed from DOM
    this.destroy();
  }

  /**
   * Remove loading state from this element (same pattern as image.liquid).
   */
  removeLoadingState() {
    this.classList.remove('is-loading');
  }

  async init() {
    try {
      // Get product ID from data attribute
      this.productId = this.dataset.productId;

      if (!this.productId) {
        console.warn(
          'mr-color-swatches: data-product-id attribute is required'
        );
        this.removeLoadingState();
        return;
      }

      // Fetch product detail data from Fredhopper API
      const data = await fetchFhrProductDetail(this.productId);

      // Extract swatch data from API response (includes Shopify image fetching)
      const result = await extractSwatchData(data, this.productId);

      this.swatchesData = result.swatchesData ?? [];
      this.productDecodedVariantAvailability =
        result.productDecodedVariantAvailability ?? {};
      this.productIds = result.productIds ?? [];

      // Trigger MR Recommendation components to refresh. With the 'await-refresh' attribute,
      // it allows the recommendations to use the cached 'window.mrRecommendations.productDetails[productId]' data without sending another API request.
      if (
        window.mrRecommendations &&
        typeof window.mrRecommendations.refresh === 'function'
      ) {
        window.mrRecommendations.refresh();
      }

      if (!this.swatchesData?.length || this.swatchesData.length < 2) {
        console.warn(
          'mr-color-swatches: No swatches data available or there is only 1 swatch. No color swatches will be rendered.'
        );
        this.removeLoadingState();
        return;
      }

      // Expose decoded availability for option-availability check (subscribeOptionSelectionChangeForSwatches)
      // Shape: { [productId]: { [optionName]: { [value]: true } } }
      window.mrColorSwatchesOptionAvailability =
        this.productDecodedVariantAvailability;

      // Render swatches
      this.render();
      this.removeLoadingState();
    } catch (error) {
      console.error('mr-color-swatches: Initialization failed', error);
      this.removeLoadingState();
      // Silently fail - don't show error on front-end
    }
  }

  // Helper to get normalized values from swatchData (supports nested keys like 'color.color_name')
  getSwatchValue(swatchData, ...keys) {
    for (const key of keys) {
      if (key.includes('.')) {
        const [parent, child] = key.split('.');
        if (swatchData[parent]?.[child] != null)
          return swatchData[parent][child];
      } else if (swatchData[key] != null) {
        return swatchData[key];
      }
    }
    return null;
  }

  render() {
    // Clear existing content
    this.innerHTML = '';

    // Get swatch style from data attribute, API response, or default
    const swatchStyle = this.dataset.swatchStyle || 'circle';

    // Render each swatch
    this.swatchesData.forEach((swatchData, index) => {
      const swatchElement = this.createSwatchElement(
        swatchData,
        index,
        swatchStyle
      );
      this.appendChild(swatchElement);
    });
  }

  /**
   * Creates a swatch element from swatch data
   * @param {SwatchData} swatchData - The swatch data object
   * @param {number} index - The index of the swatch in the array
   * @param {string} swatchStyle - The swatch style ('circle' or 'rectangle')
   * @returns {HTMLElement} The created tooltip-component element
   */
  createSwatchElement(swatchData, index, swatchStyle) {
    // Normalize swatch data values
    const productId =
      this.getSwatchValue(swatchData, 'productId', 'id') || `product-${index}`;
    const colorValue =
      this.getSwatchValue(
        swatchData,
        'color.color_name',
        'colorName',
        'colorValue',
        'value'
      ) || '';
    const url = this.getSwatchValue(swatchData, 'url', 'productUrl') || '#';
    const backgroundImage = this.getSwatchValue(
      swatchData,
      'backgroundImage',
      'swatchImage',
      'colour_swatch_image'
    );
    const backgroundColor = this.getSwatchValue(
      swatchData,
      'backgroundColor',
      'swatchColor',
      'colorHex',
      'colour_swatch_hex'
    );

    // Escape values for safe HTML injection
    const escapeHtml = (str) => {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    };

    const inputId = swatchData.inputId || `swatch-input-${productId}-${index}`;
    const isActive = swatchData.isActive || swatchData.selected;
    const checkedAttr = isActive ? 'checked' : '';
    const dataProductUrlAttr =
      url !== '#' ? `data-product-url="${escapeHtml(url)}"` : '';

    // In quickview context, use data-sibling-handle so handleSiblingSwatchClick
    // intercepts the click and reloads the quickview instead of navigating to PDP
    const isQuickView = !!this.closest('.popup-quick-view');
    const handle =
      url !== '#'
        ? (url.split('/products/')[1] || '').split('?')[0].split('/')[0]
        : null;
    const siblingHandleAttr =
      isQuickView && handle
        ? `data-sibling-handle="${escapeHtml(handle)}"`
        : '';

    // Build background style
    let backgroundStyle = '';
    if (backgroundImage) {
      backgroundStyle = `background-image: url('${escapeHtml(backgroundImage)}'); background-color: #ffffff; background-size: calc(100% - 8px); background-position: center;`;
    } else if (backgroundColor) {
      backgroundStyle = `background-color: ${escapeHtml(backgroundColor)};`;
    }
    const styleAttr = backgroundStyle ? `style="${backgroundStyle}"` : '';

    const optionAvailabilityByProduct =
      window.mrColorSwatchesOptionAvailability;
    const checkedOptions = document.querySelectorAll(
      'input[data-option-value-id]:checked'
    );
    let isUnavailable = false;
    if (optionAvailabilityByProduct && checkedOptions.length) {
      for (const btn of checkedOptions) {
        const optionNameAndValue = getOptionNameAndValueFromBtn(btn);
        if (
          optionNameAndValue &&
          optionAvailabilityByProduct[productId]?.[
            optionNameAndValue.optionName
          ]?.[optionNameAndValue.optionValue] !== true
        ) {
          isUnavailable = true;
          break;
        }
      }
    }

    const unavailableClass = isUnavailable ? ' swatch__unavailable' : '';
    const enlargedPopover = backgroundImage
      ? `<div class="swatch__enlarged-popover" style="background-image: url('${escapeHtml(backgroundImage)}');"></div>`
      : '';
    const html = `
      <tooltip-component class="swatches swatch__button swatch__button--${escapeHtml(swatchStyle)}">
        ${enlargedPopover}
        <a href="${escapeHtml(url)}" class="${unavailableClass}" aria-label="${escapeHtml(colorValue)}" data-product-id="${escapeHtml(productId)}" ${siblingHandleAttr}>
          <input
            type="radio"
            class="swatch__input"
            id="${escapeHtml(inputId)}"
            ${checkedAttr}
            ${dataProductUrlAttr}
          />
          <label
            class="swatch__label"
            for="${escapeHtml(productId)}"
            data-swatch="${escapeHtml(productId)}"
            data-tooltip="${escapeHtml(colorValue || productId)}"
            ${styleAttr}
          >
            <span class="visually-hidden">${escapeHtml(colorValue)}</span>
            <span class="icon icon-check"></span>
          </label>
        </a>
      </tooltip-component>
    `;

    // Parse HTML string and return the element
    const template = document.createElement('div');
    template.innerHTML = html.trim();
    return template.firstElementChild;
  }

  destroy() {
    // Cleanup event listeners if needed
    this.innerHTML = '';
    this.initialized = false;
  }
}

// Register the custom element
if (!customElements.get('mr-color-swatches')) {
  customElements.define('mr-color-swatches', MRColorSwatches);
}

/**
 * Parse option name and value from the option input target (e.g. name="options[Size]" value="14").
 * @param {HTMLInputElement|Element} target - The input that changed
 * @returns {{ optionName: string, optionValue: string }|null}
 */
function getOptionNameAndValueFromBtn(target) {
  if (!target) return null;
  const name = target.getAttribute?.('name') || '';
  const value = target.getAttribute?.('value') ?? target.value ?? '';
  const match = name.match(/^options\[(.+)\]$/);
  const optionName = match ? match[1].trim() : '';
  if (!optionName || value === '') return null;
  return { optionName, optionValue: String(value).trim() };
}

/**
 * Subscribe to option-value-selection-change: parse option name/value from target,
 * use option availability by product to disable colour swatches for product IDs that don't have that option available.
 */
function subscribeOptionSelectionChangeForSwatches() {
  if (
    !window.theme?.PUB_SUB_EVENTS?.optionValueSelectionChange ||
    !window.subscribe
  )
    return;

  window.subscribe(
    window.theme.PUB_SUB_EVENTS.optionValueSelectionChange,
    (payload) => {
      const target = payload?.data?.target;
      const optionNameAndValue = getOptionNameAndValueFromBtn(target);
      if (!optionNameAndValue) return;

      const { optionName, optionValue } = optionNameAndValue;

      const optionAvailabilityByProduct =
        window.mrColorSwatchesOptionAvailability;
      if (
        !optionAvailabilityByProduct ||
        typeof optionAvailabilityByProduct !== 'object'
      )
        return;

      const productIds = Object.keys(optionAvailabilityByProduct);
      if (!productIds.length) return;

      const productIdsWithOption = getProductIdsWithOptionAvailable(
        productIds,
        optionName,
        optionValue,
        optionAvailabilityByProduct
      );

      document
        .querySelectorAll('mr-color-swatches a[data-product-id]')
        .forEach((link) => {
          if (link.querySelector('input:checked')) return;

          const productId = link.getAttribute('data-product-id');
          const available = productIdsWithOption.has(productId);
          if (available) {
            link.classList.remove('swatch__unavailable');
          } else {
            link.classList.add('swatch__unavailable');
          }
        });
    }
  );
}
subscribeOptionSelectionChangeForSwatches();

/**
 * Fredhopper collection page swatches
 * Fetches swatch data, renders colour swatches, handles colour/size selection and AJAX add-to-cart
 */
const API_BASE =
  'https://b0z44gpj9l.execute-api.ap-southeast-2.amazonaws.com/live';
const storefrontCache = new Map();

function escHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function imgUrl(url, width) {
  return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
}

function srcset(url) {
  return [180, 360, 540, 720, 900]
    .map((w) => `${imgUrl(url, w)} ${w}w`)
    .join(', ');
}

function formatMoney(amount) {
  return '$' + parseFloat(amount).toFixed(2);
}

async function fetchStorefrontProduct(productId) {
  if (storefrontCache.has(productId)) return storefrontCache.get(productId);

  const query = `{
    product(id: "gid://shopify/Product/${productId}") {
      id title handle vendor
      priceRange { minVariantPrice { amount currencyCode } }
      compareAtPriceRange { minVariantPrice { amount currencyCode } }
      images(first: 2) { edges { node { url altText } } }
      variants(first: 50) { edges { node { id title availableForSale price { amount } selectedOptions { name value } } } }
    }
  }`;

  const data = await executeQuery(query);
  const product = data?.data?.product;
  if (product) storefrontCache.set(productId, product);
  return product;
}

function updateProductCard(productItem, product) {
  const productUrl = `/products/${product.handle}`;
  productItem.setAttribute(
    'data-product-id',
    product.id.replace('gid://shopify/Product/', '')
  );

  productItem.querySelectorAll('a[data-grid-link]').forEach((link) => {
    link.href = productUrl;
    if (link.hasAttribute('aria-label'))
      link.setAttribute('aria-label', product.title);
  });

  const titleEl = productItem.querySelector('.product-grid-item__title');
  if (titleEl) {
    titleEl.textContent = product.title;
    titleEl.href = productUrl;
    titleEl.setAttribute('aria-label', product.title);
  }

  const brandEl = productItem.querySelector('.product-grid-item__brand-link');
  if (brandEl) {
    brandEl.textContent = product.vendor;
    brandEl.href = `/collections/vendors?q=${encodeURIComponent(product.vendor)}`;
  }

  // Price
  const priceEl = productItem.querySelector('.product-grid-item__price');
  if (priceEl) {
    const price = parseFloat(product.priceRange?.minVariantPrice?.amount || 0);
    const compareAt = parseFloat(
      product.compareAtPriceRange?.minVariantPrice?.amount || 0
    );
    if (price === 0) {
      priceEl.style.display = 'none';
    } else {
      priceEl.style.display = '';
      priceEl.href = productUrl;
      priceEl.innerHTML =
        compareAt > price
          ? `<span class="price__sale"><span class="new-price">${formatMoney(price)}</span> <span class="old-price">${formatMoney(compareAt)}</span></span>`
          : formatMoney(price);
    }
  }

  // Images
  const images = product.images?.edges || [];
  if (images.length > 0) {
    const mainImg = productItem.querySelector('[data-main-image] img');
    if (mainImg) {
      const url = images[0].node.url;
      const alt = images[0].node.altText || product.title;
      mainImg.src = imgUrl(url, 1000);
      mainImg.alt = alt;
      mainImg.srcset = srcset(url);
      const srOnly = productItem.querySelector(
        '[data-product-media-featured] .visually-hidden'
      );
      if (srOnly) srOnly.textContent = alt;
    }
  }
  if (images.length > 1) {
    const hoverImg = productItem.querySelector(
      '[data-product-image-hover] img'
    );
    if (hoverImg) {
      const url = images[1].node.url;
      hoverImg.src = imgUrl(url, 1000);
      hoverImg.alt = images[1].node.altText || product.title;
      hoverImg.srcset = srcset(url);
    }
  }

  // Sizes
  const sizesContainer = productItem.querySelector('.product-grid-item__sizes');
  if (sizesContainer) {
    const variants = product.variants?.edges || [];
    sizesContainer.innerHTML = variants
      .filter((e) => e.node.selectedOptions?.find((o) => o.name === 'Size'))
      .map((e) => {
        const v = e.node;
        const size = v.selectedOptions.find((o) => o.name === 'Size').value;
        const vid = v.id.replace('gid://shopify/ProductVariant/', '');
        const soldOut = !v.availableForSale;
        return `<button type="button" class="product-grid-item__size${soldOut ? ' product-grid-item__size--sold-out' : ''}" data-size="${size}" data-variant-id="${vid}"${soldOut ? ' disabled' : ''}>${size}</button>`;
      })
      .join('');
    setupSizeClickHandlers(sizesContainer);
  }
}

function addToCart(sizeBtn, sizesContainer) {
  const variantId = sizeBtn.getAttribute('data-variant-id');
  if (!variantId) return;

  sizesContainer.classList.add('is-loading');
  sizeBtn.classList.add('is-loading');
  if (!sizeBtn.querySelector('.svg-loader')) {
    sizeBtn.insertAdjacentHTML(
      'beforeend',
      '<svg height="14" width="14" class="svg-loader"><circle r="5" cx="7" cy="7" /><circle stroke-dasharray="87.96459430051421 87.96459430051421" r="5" cx="7" cy="7" /></svg>'
    );
  }

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', 1);

  fetch(window.theme.routes.cart_add_url, {
    method: 'POST',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      Accept: 'application/javascript',
    },
    body: formData,
  })
    .then((r) => r.json())
    .then((response) => {
      sizesContainer.classList.remove('is-loading');
      sizeBtn.classList.remove('is-loading');
      const loader = sizeBtn.querySelector('.svg-loader');
      if (loader) loader.remove();

      if (response.status) {
        console.error('[Fredhopper] Add to cart error:', response.description);
        return;
      }

      // Deselect size and color so user must start fresh for next add
      sizesContainer
        .querySelectorAll('.product-grid-item__size')
        .forEach((btn) => btn.classList.remove('is-active'));
      const productItem = sizesContainer.closest('[data-product-id]');
      if (productItem) {
        productItem
          .querySelectorAll('product-grid-item-swatch tooltip-component')
          .forEach((tc) => tc.classList.remove('is-active'));
      }

      document.dispatchEvent(
        new CustomEvent('theme:cart:refresh', { bubbles: true })
      );
    })
    .catch((error) => {
      sizesContainer.classList.remove('is-loading');
      sizeBtn.classList.remove('is-loading');
      const loader = sizeBtn.querySelector('.svg-loader');
      if (loader) loader.remove();
      console.error('[Fredhopper] Add to cart error:', error);
    });
}

function setupSizeClickHandlers(sizesContainer) {
  if (sizesContainer.dataset.sizeHandlerSetup) return;
  sizesContainer.dataset.sizeHandlerSetup = 'true';

  sizesContainer.addEventListener('click', (e) => {
    const sizeBtn = e.target.closest('.product-grid-item__size');
    if (!sizeBtn || sizeBtn.disabled) return;

    e.preventDefault();
    e.stopPropagation();

    // Mark selected size
    sizesContainer
      .querySelectorAll('.product-grid-item__size')
      .forEach((btn) => btn.classList.remove('is-active'));
    sizeBtn.classList.add('is-active');

    // Require color selection before adding to cart
    const productItem = sizesContainer.closest('[data-product-id]');
    const swatchContainer = productItem?.querySelector(
      'product-grid-item-swatch'
    );
    const hasSwatches =
      swatchContainer?.querySelectorAll('tooltip-component').length > 0;
    if (
      hasSwatches &&
      !swatchContainer.querySelector('tooltip-component.is-active')
    )
      return;

    addToCart(sizeBtn, sizesContainer);
  });
}

function renderSwatches(productItem, productId, productData) {
  const existingSwatch = productItem.querySelector('product-grid-item-swatch');
  const swatches = productData.swatches;

  const tooltipsHtml = swatches
    .map((swatch, index) => {
      const hex = swatch.colour_hex || '#000000';
      const hexClean = hex.replace('#', '');
      const fullHex = hex.startsWith('#') ? hex : '#' + hex;
      let hiddenClass =
        index > 3 ? ' is-hidden' : index > 2 ? ' is-hidden-mobile' : '';
      const activeClass = index === 0 ? ' is-active' : '';

      let styleAttr, swatchImageAttr, linkClass;
      if (swatch.colour_image) {
        styleAttr = `--swatch: url(${escHtml(swatch.colour_image)}); --swatch-color: ${fullHex};`;
        swatchImageAttr = escHtml(swatch.colour_image);
        linkClass = 'swatch__link';
      } else {
        styleAttr = `--swatch: ${fullHex};`;
        swatchImageAttr = '';
        linkClass = 'swatch__link swatch__link--no-image';
      }

      const colourName = escHtml(swatch.colour_name || '');
      const handle = escHtml(swatch.handle || '');

      return `<tooltip-component class="swatches swatch__button${hiddenClass}${activeClass}" data-swatch-button data-value="${hexClean}" data-tooltip="${colourName}" data-fh-product-id="${swatch.product_id}" data-fh-handle="${handle}">
      <div id="${productId}-${swatch.product_id}-${index}-${hexClean}" class="swatch__label" data-swatch="${hexClean}" data-swatch-variant="${swatch.product_id}-${index}" data-swatch-image="${swatchImageAttr}" data-swatch-image-id="" data-swatch-index="${index}" style="${styleAttr}"></div>
      <a href="/products/${handle}" data-swatch-variant="${swatch.product_id}-${index}" data-deferred-trigger-element class="${linkClass}">${escHtml(productData.title)} - color variant: ${colourName}</a>
    </tooltip-component>`;
    })
    .join('');

  const countClickable = swatches.length > 3;
  const countText =
    swatches.length === 1 ? '1 colour' : `${swatches.length} colours`;
  const countHtml = `<span class="swatch__colour-count${countClickable ? ' swatch__colour-count--clickable' : ''}"${countClickable ? ' data-swatches-more role="button" tabindex="0"' : ''}>${countText}</span>`;

  const fullHtml = `<product-grid-item-swatch class="product-grid-item__swatch product-grid__swatch--rectangle" data-swatch-handle="${escHtml(productData.handle)}" data-swatch-label="Color">${tooltipsHtml}${countHtml}</product-grid-item-swatch>`;

  if (existingSwatch) {
    existingSwatch.outerHTML = fullHtml;
  } else {
    const infoDiv = productItem.querySelector('.product-grid-item__info');
    if (infoDiv) infoDiv.insertAdjacentHTML('beforeend', fullHtml);
  }
}

function setupSwatchClickHandlers(productItem) {
  const swatchContainer = productItem.querySelector('product-grid-item-swatch');
  if (!swatchContainer) return;

  swatchContainer.addEventListener('click', async (e) => {
    const tooltip = e.target.closest('tooltip-component');
    if (!tooltip) return;

    e.preventDefault();
    e.stopPropagation();

    const clickedProductId = tooltip.getAttribute('data-fh-product-id');
    if (!clickedProductId) return;

    // Mark active colour
    swatchContainer.querySelectorAll('tooltip-component').forEach((tc) => {
      tc.classList.toggle(
        'is-active',
        tc.getAttribute('data-fh-product-id') === clickedProductId
      );
    });

    // Remember selected size before card re-renders
    const selectedSize = productItem
      .querySelector('.product-grid-item__size.is-active')
      ?.getAttribute('data-size');

    const currentProductId = productItem.getAttribute('data-product-id');
    if (clickedProductId === currentProductId) {
      // Same product - if size already selected, add to cart
      if (selectedSize) {
        const sizeBtn = productItem.querySelector(
          `.product-grid-item__size[data-size="${selectedSize}"]`
        );
        if (sizeBtn && !sizeBtn.disabled) sizeBtn.click();
      }
      return;
    }

    try {
      const product = await fetchStorefrontProduct(clickedProductId);
      if (!product) {
        window.location.href = `/products/${tooltip.getAttribute('data-fh-handle')}`;
        return;
      }

      updateProductCard(productItem, product);

      // Re-select previously chosen size and add to cart
      if (selectedSize) {
        const newSizeBtn = productItem.querySelector(
          `.product-grid-item__size[data-size="${selectedSize}"]`
        );
        if (newSizeBtn && !newSizeBtn.disabled) newSizeBtn.click();
      }
    } catch (error) {
      console.error('[Fredhopper] Error:', error);
      window.location.href = `/products/${tooltip.getAttribute('data-fh-handle')}`;
    }
  });
}

async function initFredhopperSwatches(sectionId) {
  const section = document.querySelector(`[data-section-id="${sectionId}"]`);
  if (!section) return;

  const productIds = [
    ...new Set(
      [...section.querySelectorAll('[data-product-id]')]
        .map((el) => el.getAttribute('data-product-id'))
        .filter(Boolean)
    ),
  ];
  if (!productIds.length) return;

  try {
    // Fetch swatch data from Fredhopper
    const params = new URLSearchParams({
      fh_location: `//catalog01/en_GB/secondid<{${productIds.join(',')}}`,
      fh_view: 'lister',
      fh_view_size: Math.max(19, productIds.length),
    });

    const response = await fetch(
      `${API_BASE}/public/v1/fredhopper/query?${params}`
    );
    if (!response.ok) return;

    const data = await response.json();
    const universes = data.universes?.universe || [];
    const store = universes.find((u) => u.name === 'catalog01');
    if (!store?.['items-section']?.items?.item) return;

    // Process and apply swatch data
    for (const item of store['items-section'].items.item) {
      const attrs = {};
      for (const attr of item.attribute) {
        const values = (attr.value || []).map((v) => v.value);
        attrs[attr.name] = values.length === 1 ? values[0] : values;
      }

      // secondid from FH API lister view is now a list of variant IDs. Use 'primary_id' for product ID.
      const productId = attrs.primary_id;
      if (!productId) continue;

      const productItem = section.querySelector(
        `[data-product-id="${productId}"]`
      );
      if (!productItem) continue;

      let swatches = [];
      if (attrs.swatch_info) {
        try {
          swatches = JSON.parse(attrs.swatch_info);
          // FH API has updated 'swatch_info' to include 'active' colour swatch as the first item in the array.
          // Filter out duplicate colour swatches in case some 'swatch_info' attributes do not have this change yet.
          if (Array.isArray(swatches)) {
            const seenProductIds = new Set();
            swatches = swatches.filter((item) => {
              if (seenProductIds.has(item.product_id)) return false;
              seenProductIds.add(item.product_id);
              return true;
            });
          }
        } catch {
          /* skip */
        }
      }

      if (swatches.length > 0) {
        renderSwatches(productItem, productId, {
          title: attrs.title,
          handle: attrs.handle,
          swatches,
        });
        setupSwatchClickHandlers(productItem);
      }

      // Set up AJAX add-to-cart on size buttons
      const sizesContainer = productItem.querySelector(
        '.product-grid-item__sizes'
      );
      if (sizesContainer) setupSizeClickHandlers(sizesContainer);
    }
  } finally {
    // Fallback: ensure all products with size buttons have click handlers,
    // even if they were not returned by the Fredhopper API.
    section.querySelectorAll('[data-product-id]').forEach((productItem) => {
      const sizesContainer = productItem.querySelector(
        '.product-grid-item__sizes'
      );
      if (sizesContainer) setupSizeClickHandlers(sizesContainer);
    });
  }
}

// Auto-initialize for featured collection sections
document.addEventListener('DOMContentLoaded', () => {
  document
    .querySelectorAll('[data-section-type="featured-collection"]')
    .forEach((section) => {
      const sectionId = section.getAttribute('data-section-id');
      if (sectionId) initFredhopperSwatches(sectionId);
    });
});

export { MRColorSwatches, initFredhopperSwatches };
